/**
 * API client — semua request ke one-workspace-be.
 * Auth menggunakan httpOnly cookie yang di-set saat login,
 * sehingga tidak ada token yang dipegang oleh JavaScript.
 */

import type {
  WorkspaceResponse,
  CreateWorkspaceRequest,
  UpdateWorkspaceRequest,
  WorkspaceMemberResponse,
  InviteMemberRequest,
  UpdateMemberRoleRequest,
} from '@/types/workspace'
import type {
  PipelineStatusResponse,
  CreatePipelineStatusRequest,
  UpdatePipelineStatusRequest,
} from '@/types/pipeline'
import type {
  ProjectResponse,
  CreateProjectRequest,
  UpdateProjectRequest,
  UpdateProjectModulesRequest,
  ProjectModulesResponse,
} from '@/types/project'
import type {
  TaskResponse,
  DashboardTaskResponse,
  CreateTaskRequest,
  UpdateTaskRequest,
  TaskFilters,
} from '@/types/task'

const BASE = import.meta.env.VITE_API_URL

// Flag untuk mencegah multiple refresh request berjalan bersamaan
let isRefreshing = false

/**
 * Wrapper fetch yang otomatis:
 * 1. Menyertakan cookie di setiap request (credentials: include)
 * 2. Jika respons 401, coba refresh token sekali lalu retry
 * 3. Jika refresh gagal, redirect ke /login
 */
async function fetchWithAuth(path: string, options: RequestInit = {}, retry = true): Promise<Response> {
  // FormData body: biarkan browser set Content-Type + boundary otomatis
  const isFormData = options.body instanceof FormData
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: isFormData
      ? (options.headers as Record<string, string> | undefined) ?? {}
      : {
          'Content-Type': 'application/json',
          ...(options.headers as Record<string, string>),
        },
  })

  if (res.status === 401 && retry && !isRefreshing) {
    isRefreshing = true
    try {
      const refreshRes = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      if (refreshRes.ok) {
        isRefreshing = false
        return fetchWithAuth(path, options, false)
      }
    } catch {
      // Abaikan error refresh
    }
    isRefreshing = false
    if (window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
    throw new Error('Session expired')
  }

  return res
}

async function get<T>(path: string): Promise<T> {
  const res = await fetchWithAuth(path)
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? 'Request failed')
  }
  return res.json() as Promise<T>
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetchWithAuth(path, {
    method: 'POST',
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? 'Request failed')
  }
  return res.json() as Promise<T>
}

async function patch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetchWithAuth(path, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? 'Request failed')
  }
  return res.json() as Promise<T>
}

async function del(path: string): Promise<void> {
  const res = await fetchWithAuth(path, { method: 'DELETE' })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? 'Request failed')
  }
}

async function getPublic<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? 'Request failed')
  }
  return res.json() as Promise<T>
}

// ── Auth API ───────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string
  email: string
  display_name?: string | null
  avatar_url?: string | null
  [key: string]: unknown
}

export interface UserProfileResponse {
  id: string
  email: string | null
  display_name: string | null
  avatar_url: string | null
}

export interface UpdateProfileRequest {
  display_name?: string | null
  avatar_url?: string | null
}

export const authApi = {
  login: async (email: string, password: string): Promise<void> => {
    const res = await fetch(`${BASE}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }))
      throw new Error((err as { error?: string }).error ?? 'Login gagal')
    }
  },

  logout: async (): Promise<void> => {
    await fetch(`${BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    })
  },

  me: (): Promise<AuthUser> => get<AuthUser>('/auth/me'),
}

// ── Workspace API ──────────────────────────────────────────────────────────────

export const workspaceApi = {
  // Workspaces
  getWorkspaces: () => get<WorkspaceResponse[]>('/workspaces'),
  getWorkspace: (slug: string) => get<WorkspaceResponse>(`/workspaces/${slug}`),
  createWorkspace: (data: CreateWorkspaceRequest) => post<WorkspaceResponse>('/workspaces', data),
  updateWorkspace: (slug: string, data: UpdateWorkspaceRequest) =>
    patch<WorkspaceResponse>(`/workspaces/${slug}`, data),
  deleteWorkspace: (slug: string) => del(`/workspaces/${slug}`),

  // Members
  getMembers: (workspaceSlug: string) =>
    get<WorkspaceMemberResponse[]>(`/members?workspace=${encodeURIComponent(workspaceSlug)}`),
  inviteMember: (workspaceSlug: string, data: InviteMemberRequest) =>
    post<unknown>(`/members?workspace=${encodeURIComponent(workspaceSlug)}`, data),
  updateMemberRole: (workspaceSlug: string, memberId: string, data: UpdateMemberRoleRequest) =>
    patch<unknown>(`/members/${memberId}?workspace=${encodeURIComponent(workspaceSlug)}`, data),
  removeMember: (workspaceSlug: string, memberId: string) =>
    del(`/members/${memberId}?workspace=${encodeURIComponent(workspaceSlug)}`),

  // Public
  checkSlug: (slug: string) =>
    getPublic<{ available: boolean; reason?: string; slug?: string }>(
      `/public/check-slug?slug=${encodeURIComponent(slug)}`
    ),
}

// ── Pipeline API ───────────────────────────────────────────────────────────────

export const pipelineApi = {
  getStatuses: (workspaceSlug: string) =>
    get<PipelineStatusResponse[]>(`/pipelines?workspace=${encodeURIComponent(workspaceSlug)}`),
  createStatus: (workspaceSlug: string, data: CreatePipelineStatusRequest) =>
    post<PipelineStatusResponse>(`/pipelines?workspace=${encodeURIComponent(workspaceSlug)}`, data),
  updateStatus: (workspaceSlug: string, statusId: string, data: UpdatePipelineStatusRequest) =>
    patch<PipelineStatusResponse>(
      `/pipelines/${statusId}?workspace=${encodeURIComponent(workspaceSlug)}`,
      data
    ),
  deleteStatus: (workspaceSlug: string, statusId: string) =>
    del(`/pipelines/${statusId}?workspace=${encodeURIComponent(workspaceSlug)}`),
}

// ── Project API ────────────────────────────────────────────────────────────────

export const projectApi = {
  getProjects: (workspaceSlug: string) =>
    get<ProjectResponse[]>(`/projects?workspace=${encodeURIComponent(workspaceSlug)}`),
  getProject: (workspaceSlug: string, projectId: string) =>
    get<ProjectResponse>(`/projects/${projectId}?workspace=${encodeURIComponent(workspaceSlug)}`),
  createProject: (workspaceSlug: string, data: CreateProjectRequest) =>
    post<ProjectResponse>(`/projects?workspace=${encodeURIComponent(workspaceSlug)}`, data),
  updateProject: (workspaceSlug: string, projectId: string, data: UpdateProjectRequest) =>
    patch<ProjectResponse>(
      `/projects/${projectId}?workspace=${encodeURIComponent(workspaceSlug)}`,
      data
    ),
  deleteProject: (workspaceSlug: string, projectId: string) =>
    del(`/projects/${projectId}?workspace=${encodeURIComponent(workspaceSlug)}`),
  getProjectModules: (workspaceSlug: string, projectId: string) =>
    get<ProjectModulesResponse>(
      `/projects/${projectId}/modules?workspace=${encodeURIComponent(workspaceSlug)}`
    ),
  updateProjectModules: (
    workspaceSlug: string,
    projectId: string,
    data: UpdateProjectModulesRequest
  ) =>
    patch<ProjectModulesResponse>(
      `/projects/${projectId}/modules?workspace=${encodeURIComponent(workspaceSlug)}`,
      data
    ),
}

// ── Task API ───────────────────────────────────────────────────────────────────

export const taskApi = {
  getTasks: (workspaceSlug: string, filters?: TaskFilters) => {
    const params = new URLSearchParams({ workspace: workspaceSlug })
    if (filters?.project) params.set('project', filters.project)
    if (filters?.status) params.set('status', filters.status)
    if (filters?.priority) params.set('priority', filters.priority)
    return get<TaskResponse[]>(`/tasks?${params}`)
  },
  getTask: (workspaceSlug: string, taskId: string) =>
    get<TaskResponse>(`/tasks/${taskId}?workspace=${encodeURIComponent(workspaceSlug)}`),
  createTask: (workspaceSlug: string, data: CreateTaskRequest) =>
    post<TaskResponse>(`/tasks?workspace=${encodeURIComponent(workspaceSlug)}`, data),
  updateTask: (workspaceSlug: string, taskId: string, data: UpdateTaskRequest) =>
    patch<TaskResponse>(
      `/tasks/${taskId}?workspace=${encodeURIComponent(workspaceSlug)}`,
      data
    ),
  deleteTask: (workspaceSlug: string, taskId: string) =>
    del(`/tasks/${taskId}?workspace=${encodeURIComponent(workspaceSlug)}`),
  getTodayTasks: () => {
    // en-CA locale gives YYYY-MM-DD in the browser's LOCAL timezone — avoids UTC mismatch
    const localDate = new Date().toLocaleDateString('en-CA')
    return get<DashboardTaskResponse[]>(`/tasks/today?date=${localDate}`)
  },
}

// ── Profile API ────────────────────────────────────────────────────────────────

/**
 * Multipart form upload — does NOT set Content-Type so browser can set correct
 * multipart/form-data boundary automatically.
 */
async function postForm<T>(path: string, formData: FormData): Promise<T> {
  const res = await fetchWithAuth(path, { method: 'POST', body: formData })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? 'Upload gagal')
  }
  return res.json() as Promise<T>
}

export const profileApi = {
  getProfile: (): Promise<UserProfileResponse> =>
    get<UserProfileResponse>('/users/profile'),

  updateProfile: (data: UpdateProfileRequest): Promise<UserProfileResponse> =>
    patch<UserProfileResponse>('/users/profile', data),

  uploadAvatar: (webpBlob: Blob): Promise<{ url: string }> => {
    const fd = new FormData()
    fd.append('file', webpBlob, `avatar-${Date.now()}.webp`)
    return postForm<{ url: string }>('/users/avatar', fd)
  },
}

// ── Image Upload API ───────────────────────────────────────────────────────────

export const imageApi = {
  uploadImage: (webpBlob: Blob): Promise<{ url: string }> => {
    const fd = new FormData()
    fd.append('file', webpBlob, `task-img-${Date.now()}.webp`)
    return postForm<{ url: string }>('/uploads/image', fd)
  },
}
